import { useState } from 'react';
import { Access, AccessSource, EmployeeType } from '@prisma/client';
import { Input, InputProps, Radio, RadioChangeEvent, Space, theme, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { SourceAccessItem } from '@app/domain-access-interfaces';
import { AgsEntitlementRoo, AgsRoleRoo, isAgsEntitlementRoo, isAgsRoleRoo } from '@app/integ-ags-interface';
import { Config, Tables } from '@app/web-components';

import { useFindAgsAccesses } from '../../../../bundles';
import { EmployeeTypeRenderer, useAccessSelectTable } from '../../../atoms';

interface AgsMessageProps {
  agsRecord: AgsEntitlementRoo | AgsRoleRoo;
  config: Config.Config;
}

const AgsMessage = ({ agsRecord, config }: AgsMessageProps) => {
  const { token } = theme.useToken();
  const agsEntity = isAgsRoleRoo(agsRecord) ? 'role' : 'entitlement';

  if (!agsRecord.meta.requestableByWebService) {
    return (
      <div style={{ color: token.colorWarning, fontSize: 'small' }}>
        {`This ${agsEntity} is not configured in AGS to be requestable through the AGS web service and therefore cannot be used by SAAF.`}
      </div>
    );
  }

  if (!agsRecord.meta.requestableByUI) {
    return (
      <div style={{ color: token.colorInfo, fontSize: 'small' }}>
        {`Note: This ${agsEntity} is not configured in AGS to be requested through the AGS UI but can be requested through SAAF if added to a bundle.`}
      </div>
    );
  }

  return undefined;
};

export type AgsSearchPanelProps = {
  disabled?: Tables.SelectTableDisabledType<SourceAccessItem | Access>;
  mode?: 'single' | 'multiple';
  value: (SourceAccessItem | Access)[];
  onChange?: (newValue: (SourceAccessItem | Access)[]) => void;
  allowNonRequestableRoles?: boolean;
  searchText: string;
  searchInputProps?: InputProps;
};

export const AgsSearchPanel = ({
  disabled,
  searchText,
  searchInputProps,
  allowNonRequestableRoles,
  ...props
}: AgsSearchPanelProps) => {
  const [source, setSource] = useState<AccessSource>(AccessSource.AGS_ROLE);
  const config = Config.useConfig();

  const columns: ColumnsType<SourceAccessItem> = [
    {
      title: 'Emp Type',
      dataIndex: 'employeeType',
      width: 110,
      render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (_, record) => {
        if (isAgsRoleRoo(record) || isAgsEntitlementRoo(record)) {
          return (
            <>
              <div>{record.name.replace(/ /g, '\u00a0')}</div>
              {record.name && record.meta?.displayName && record.name !== record.meta.displayName && (
                <Typography.Text type="secondary">
                  a.k.a. {record.meta.displayName.replace(/ /g, '\u00a0')}
                </Typography.Text>
              )}
              <AgsMessage agsRecord={record} config={config} />
            </>
          );
        }

        return record.name;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
  ];

  // server side table state
  const { getQuery, getTableProps } = Tables.useTableState<SourceAccessItem>('AGS Search', columns, {
    disableSession: true,
  });
  const query = getQuery();
  const queryResult = useFindAgsAccesses(source, searchText, query.take, query.skip + 1);

  const { getProps } = useAccessSelectTable<SourceAccessItem>();

  const disableSelect = (record: SourceAccessItem) =>
    (isAgsRoleRoo(record) || isAgsEntitlementRoo(record)) &&
    !record.meta.requestableByWebService &&
    !allowNonRequestableRoles;

  const handleSourceChange = (e: RadioChangeEvent) => setSource(e.target.value);

  const tooltipText = `Search AGS ${
    source === AccessSource.AGS_ENTITLEMENT ? 'Entitlement' : 'Role'
  } by name. NOTE: This does not search by display name.`;

  const tableProps = getTableProps(queryResult, { pagination: { showSizeChanger: false } });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space size="large">
        <Tooltip trigger={['focus']} title={tooltipText} placement="top">
          <Input style={{ width: 650 }} placeholder={tooltipText} {...searchInputProps} />
        </Tooltip>
        <Radio.Group value={source} onChange={handleSourceChange}>
          <Radio value={AccessSource.AGS_ROLE}>AGS Role</Radio>
          <Radio value={AccessSource.AGS_ENTITLEMENT}>AGS Entitlement</Radio>
        </Radio.Group>
      </Space>
      <Tables.SelectTable
        {...getProps()}
        columns={columns}
        dataSource={queryResult.data?.data}
        disabled={
          typeof disabled === 'boolean'
            ? disabled
            : {
                remove: disabled?.remove,
                select: (record) =>
                  disableSelect(record) ||
                  (typeof disabled?.select === 'function' ? disabled.select(record) : !!disabled?.select),
              }
        }
        loading={queryResult.isFetching}
        pagination={tableProps.pagination}
        scroll={{ y: 380 }}
        onTableChange={tableProps.onChange}
        {...props}
      />
    </Space>
  );
};
